/**
 * src/components/assets/AssetImportExport.jsx
 * Route: /assets/import-export
 * Handles Excel export (with live master data) and import
 */
import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
function resolveApiBase() {
  if (process.env.REACT_APP_API_URL)
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return 'http://localhost:8000/api';
  return `${protocol}//${hostname}/api`;   // same-domain reverse-proxy
}
const API      = resolveApiBase();
//const API   = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const token = () => localStorage.getItem('iassets_token');

const Card = ({ children, style }) => (
  <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0',
    boxShadow:'0 1px 4px rgba(0,0,0,.06)', ...style }}>
    {children}
  </div>
);

export default function AssetImportExport() {
  const [exporting,  setExporting]  = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [result,     setResult]     = useState(null);
  const [file,       setFile]       = useState(null);
  const fileRef = useRef(null);

  /* ── Export ─────────────────────────────────────────────── */
  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API}/excel/assets/export`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `iAssets_Assets_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded — master data pre-filled in reference sheets');
    } catch(e) {
      toast.error('Export failed: ' + e.message);
    } finally { setExporting(false); }
  };

  /* ── Import ─────────────────────────────────────────────── */
  const handleImport = async () => {
    if (!file) { toast.error('Please select an Excel file'); return; }
    setImporting(true);
    setResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await fetch(`${API}/excel/assets/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || JSON.stringify(data));
      setResult(data);
      toast.success(`Import complete — ${data.created} created, ${data.updated} updated`);
    } catch(e) {
      toast.error('Import failed: ' + e.message);
    } finally { setImporting(false); }
  };

  const LS = { fontSize:12, fontWeight:700, color:'#475569', display:'block',
    marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' };

  return (
    <div style={{ maxWidth:780, fontFamily:'-apple-system,sans-serif' }}>

      {/* Page Header */}
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:'#1a1f2e', margin:0 }}>
          📊 Asset Import / Export
        </h1>
        <p style={{ fontSize:13, color:'#64748b', marginTop:6 }}>
          Export all assets to Excel (with master data pre-filled) · Fill in new assets · Re-import to update the database.
        </p>
      </div>

      {/* HOW IT WORKS */}
      <Card style={{ padding:'16px 20px', marginBottom:20, background:'#f0f6ff',
        border:'1px solid #bfdbfe' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#1a56db', marginBottom:10 }}>
          📋 How it works
        </div>
        <div style={{ display:'flex', gap:0, flexDirection:'column' }}>
          {[
            ['1', 'Click Export Excel', 'Downloads an Excel file with all current assets + master data (Categories, Locations, Users etc.) pre-filled in the reference sheets.'],
            ['2', 'Fill / Edit Assets',  'On the "Asset Import" sheet, add new rows or edit existing ones. Use IDs from the reference sheets for lookups.'],
            ['3', 'Import the File',     'Upload the filled Excel below. Existing assets (matched by Asset Code) will be updated; new rows will be created.'],
          ].map(([n, title, desc]) => (
            <div key={n} style={{ display:'flex', gap:12, padding:'8px 0',
              borderBottom:'1px solid #dbeafe' }}>
              <span style={{ width:22, height:22, borderRadius:'50%', background:'#1a56db',
                color:'#fff', fontSize:11, fontWeight:800, display:'flex',
                alignItems:'center', justifyContent:'center', flexShrink:0 }}>{n}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:'#1e40af' }}>{title}</div>
                <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>

        {/* EXPORT CARD */}
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📤 Export Assets
          </div>
          <p style={{ fontSize:12, color:'#64748b', marginBottom:16, lineHeight:1.6 }}>
            Downloads all assets with master reference data (Categories, Locations, Users, Vendors, Dep Types, Warranty Periods) pre-filled in the reference sheets.
          </p>
          <button onClick={handleExport} disabled={exporting}
            style={{ width:'100%', padding:'12px', background: exporting?'#93c5fd':'#1a56db',
              color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:700,
              cursor: exporting?'not-allowed':'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export to Excel'}
          </button>
        </Card>

        {/* IMPORT CARD */}
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:6 }}>
            📥 Import Assets
          </div>
          <p style={{ fontSize:12, color:'#64748b', marginBottom:12, lineHeight:1.6 }}>
            Upload the filled Excel file. Existing assets matched by <strong>Asset Code</strong> will be updated; new asset codes will be created.
          </p>

          {/* File picker */}
          <div onClick={() => fileRef.current?.click()}
            style={{ border:`2px dashed ${file?'#1a56db':'#e2e8f0'}`,
              borderRadius:9, padding:'16px', textAlign:'center',
              cursor:'pointer', marginBottom:12, background: file?'#eff6ff':'#f8fafc' }}>
            <input ref={fileRef} type="file" accept=".xlsx"
              style={{ display:'none' }}
              onChange={e => { setFile(e.target.files[0]); setResult(null); }}/>
            {file ? (
              <div>
                <div style={{ fontSize:24 }}>📄</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#1a56db' }}>{file.name}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>
                  {(file.size/1024).toFixed(1)} KB
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:28 }}>📂</div>
                <div style={{ fontSize:13, color:'#64748b' }}>Click to select Excel file</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>.xlsx only</div>
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:8 }}>
            {file && (
              <button onClick={() => { setFile(null); setResult(null); fileRef.current.value=''; }}
                style={{ padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:8,
                  background:'#fff', fontSize:13, cursor:'pointer', color:'#64748b' }}>
                ✕ Clear
              </button>
            )}
            <button onClick={handleImport} disabled={importing || !file}
              style={{ flex:1, padding:'12px', background: importing?'#93c5fd': !file?'#e2e8f0':'#16a34a',
                color: !file?'#94a3b8':'#fff', border:'none', borderRadius:9,
                fontSize:14, fontWeight:700, cursor: (importing||!file)?'not-allowed':'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              {importing ? '⏳ Importing…' : '⬆ Import Excel'}
            </button>
          </div>
        </Card>
      </div>

      {/* IMPORT RESULT */}
      {result && (
        <Card style={{ padding:'20px' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e', marginBottom:14 }}>
            ✅ Import Result
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:14 }}>
            {[
              { label:'Created',   val:result.created,  color:'#16a34a', bg:'#f0fdf4' },
              { label:'Updated',   val:result.updated,  color:'#1a56db', bg:'#eff6ff' },
              { label:'Skipped',   val:result.skipped,  color:'#f59e0b', bg:'#fefce8' },
              { label:'Total',     val:result.total_rows_processed, color:'#374151', bg:'#f8fafc' },
            ].map(s => (
              <div key={s.label} style={{ background:s.bg, borderRadius:9,
                padding:'12px', textAlign:'center', border:`1px solid ${s.color}33` }}>
                <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.val}</div>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {result.errors?.length > 0 && (
            <div style={{ background:'#fff1f2', border:'1px solid #fecaca',
              borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:8 }}>
                ⚠ Errors ({result.errors.length})
              </div>
              <div style={{ fontSize:11, color:'#7f1d1d', lineHeight:1.8, fontFamily:'monospace',
                maxHeight:180, overflowY:'auto' }}>
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* COLUMN REFERENCE */}
      <Card style={{ padding:'20px', marginTop:20 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#1a1f2e', marginBottom:12 }}>
          📑 Column Reference
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
          {[
            ['Asset Code *',        'asset_code — unique, e.g. AST-001'],
            ['Asset Name *',        'name — full asset name'],
            ['Category ID',         'From Categories sheet'],
            ['Location ID',         'From Locations sheet'],
            ['Assigned To (User ID)','From Users sheet'],
            ['Vendor ID',           'From Vendors sheet'],
            ['Dep Type ID',         'From Dep Types sheet'],
            ['Warranty Period ID',  'From Warranty Periods sheet'],
            ['Status',              'operational/maintenance/breakdown/retired'],
            ['Dates',               'Format: YYYY-MM-DD'],
            ['Y/N columns',         'Enter Y or N only'],
            ['Depreciation Method', 'slm or wdv'],
          ].map(([k, v]) => (
            <div key={k} style={{ display:'flex', gap:8, padding:'6px 8px',
              background:'#f8fafc', borderRadius:6, fontSize:12 }}>
              <span style={{ fontWeight:700, color:'#1a56db', flexShrink:0 }}>{k}:</span>
              <span style={{ color:'#64748b' }}>{v}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
